import type { Schedule } from "@medplum/fhirtypes";
import { getTRPCClient, LOCATION_ENTITY_ID_SYSTEM } from "@repo/api";
import {
  type CreateLocationInput,
  createLocationInputSchema,
  locationIdSchema,
  type UpdateLocationInput,
  updateLocationInputSchema,
} from "@repo/api/schemas";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useMemo } from "react";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import type { LocationRow } from "../fhir/types";
import { optimisticSchedule } from "./_schedule-optimistic";
import { countSchedulesByActor } from "./shared/schedule-counts";
import type { Address, CodeableConcept, ContactPoint, DayOfWeek } from "./shared/types";

/**
 * Location — normalized read-model.
 * `partOfLocationId` flattens the parent reference to a plain id.
 */
export interface LocationDto {
  locationId: UpdateLocationInput["id"];
  status?: "active" | "suspended" | "inactive";

  name?: string;
  alias?: string[];
  description?: string;

  mode?: "instance" | "kind";
  type?: CodeableConcept[];
  physicalType?: CodeableConcept;

  address?: Address;
  telecom?: ContactPoint[];

  hoursOfOperation?: Array<{
    daysOfWeek?: DayOfWeek[];
    allDay?: boolean;
    openingTime?: string;
    closingTime?: string;
  }>;

  partOfLocationId?: UpdateLocationInput["id"];

  updatedAt?: string;
  /** Resolved from `partOfLocationId` — the parent's name, for a list's subtitle. */
  parentName?: string;
  /** Whether any other location is `partOf` this one. */
  hasChildren: boolean;
  /** Schedules whose actor is this location. Booking reads it; the catalog shows it. */
  scheduleCount: number;
  /** Ancestry joined with " > ", ending in this location's own name. */
  displayPath: string;
}

const LOCATION_COLLECTION = "location";
const SCHEDULE_COLLECTION = "schedule";

function toDto(l: LocationRow): LocationDto {
  return {
    locationId: locationIdSchema.parse(l.domainId),
    status: l.status as LocationDto["status"],
    name: l.name,
    alias: l.alias?.length ? [...l.alias] : undefined,
    description: l.description,
    mode: l.mode as LocationDto["mode"],
    type: l.type as CodeableConcept[] | undefined,
    physicalType: l.physicalType as CodeableConcept | undefined,
    address: l.address as Address | undefined,
    telecom: l.telecom as ContactPoint[] | undefined,
    hoursOfOperation: l.hoursOfOperation?.map((h) => ({
      daysOfWeek: h.daysOfWeek as DayOfWeek[] | undefined,
      allDay: h.allDay,
      openingTime: h.openingTime,
      closingTime: h.closingTime,
    })),
    updatedAt: l.meta?.lastUpdated,
    // Enriched by `enrichLocations` once the whole set is known.
    hasChildren: false,
    scheduleCount: 0,
    displayPath: l.name ?? "Unnamed Location",
    partOfLocationId: l.partOfDomainId ? locationIdSchema.parse(l.partOfDomainId) : undefined,
  };
}

/** The three location types, held in Location.physicalType.text. */
export type LocationPhysicalType = "Site" | "Room" | "Group";

interface UseLocationsOptions {
  status?: LocationDto["status"];
  /** Restrict to one location type ("Site" | "Room" | "Group"). */
  physicalType?: LocationPhysicalType;
  /**
   * Deregistering is a soft delete — the Location stays, marked inactive — so
   * every list has to drop those itself or a location comes back the moment
   * the subscription echoes it. Only a lookup by id wants them (see
   * `useLocation`: a direct link to a retired room should still render).
   */
  includeInactive?: boolean;
}

interface UseLocationsResult {
  locations: LocationDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

/**
 * Ancestry chain for a location, ending in its own name.
 * Guards against a cyclic `partOf`, which the server does not forbid.
 */
function displayPathOf(id: string, byId: Map<string, LocationDto>): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined = id;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const node: LocationDto | undefined = byId.get(cursor);
    if (!node) break;
    parts.unshift(node.name ?? "Unnamed Location");
    cursor = node.partOfLocationId;
  }
  return parts.join(" > ");
}

/**
 * Fill the fields that need more than the one resource: the parent's name,
 * whether anything hangs off it, and how many schedules point at it. Resolved
 * here so no consumer holds three collections to render a row.
 */
function enrichLocations(rows: LocationDto[], schedules: Schedule[]): LocationDto[] {
  const byId = new Map(rows.map((l) => [l.locationId, l]));
  const childCount = new Set(rows.map((l) => l.partOfLocationId).filter(Boolean) as string[]);
  const scheduleCounts = countSchedulesByActor(schedules, "Location");

  return rows.map((l) => {
    const parentName = l.partOfLocationId ? byId.get(l.partOfLocationId)?.name : undefined;
    return {
      ...l,
      ...(parentName ? { parentName } : {}),
      hasChildren: childCount.has(l.locationId),
      scheduleCount: scheduleCounts.get(l.locationId) ?? 0,
      displayPath: displayPathOf(l.locationId, byId),
    };
  });
}

export function useLocations(options: UseLocationsOptions = {}): UseLocationsResult {
  const collection = useCollection(LOCATION_COLLECTION);
  const scheduleCollection = useCollection(SCHEDULE_COLLECTION);
  const { status, physicalType, includeInactive = false } = options;

  const scheduleResult = useLiveQuery({
    query: (q) => q.from({ schedule: scheduleCollection }).select(({ schedule }) => ({ ...schedule })),
  });

  const result = useLiveQuery({
    queryKey: [collection.id, "locations", status ?? null, physicalType ?? null],
    query: (q) => {
      let query = q.from({ location: collection });
      if (status !== undefined) {
        query = query.where(({ location }) => eq(location.status, status));
      }
      if (physicalType !== undefined) {
        query = query.where(({ location }) => eq(location.physicalType?.text, physicalType));
      }
      return query.fn.select(({ location }) => toDto(location as LocationRow));
    },
  });

  // Filtered here rather than in the query: a location with no `status` at all
  // is in service, and a `ne` comparison against a missing field drops it.
  const locations = useMemo(() => {
    const all = (result.data as LocationDto[] | undefined) ?? [];
    const visible = includeInactive || status !== undefined ? all : all.filter((l) => l.status !== "inactive");
    return enrichLocations(visible, (scheduleResult.data as Schedule[] | undefined) ?? []);
  }, [result.data, includeInactive, status, scheduleResult.data]);

  return {
    locations,
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

interface UseLocationResult {
  location: LocationDto | null;
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

export function useLocation(locationId: UpdateLocationInput["id"] | undefined): UseLocationResult {
  const { locations, isLoading, isReady, isError } = useLocations({ includeInactive: true });
  const location = useMemo(() => {
    if (!locationId) return null;
    return locations.find((l) => l.locationId === locationId) ?? null;
  }, [locations, locationId]);
  return { location, isLoading, isReady, isError };
}

const locations = () => getDataStoreClient().getCollection("location");

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function resourceIdForDomainId(domainId: string): string {
  if (!locations().has(domainId)) throw new Error(`Location '${domainId}' is not loaded`);
  return domainId;
}

/** Intent: "register a location" */
export const registerLocation = defineWrite<CreateLocationInput>({
  optimistic: (params) => {
    const input = createLocationInputSchema.parse(params);
    const optimisticDomainId = input.id;
    locations().insert({
      resourceType: "Location",
      identifier: [{ system: LOCATION_ENTITY_ID_SYSTEM, value: optimisticDomainId }],
      domainId: optimisticDomainId,
      name: input.name,
      status: input.status,
      description: input.description,
      type: input.type,
      physicalType: input.physicalType,
      address: input.address,
      telecom: input.telecom,
      hoursOfOperation: input.hoursOfOperation,
      partOf: input.partOfId ? { identifier: { system: LOCATION_ENTITY_ID_SYSTEM, value: input.partOfId } } : undefined,
      partOfDomainId: input.partOfId,
    } as LocationRow);
    getDataStoreClient()
      .getCollection("schedule")
      .insert(
        optimisticSchedule({
          id: input.scheduleId,
          actor: { type: "Location", id: input.id },
          active: true,
        }),
      );
  },
  persist: async (params, options) => {
    await getTRPCClient().resources.registerLocation.mutate(params, options);
  },
});

/** Intent: "update a location" */
export const updateLocation = defineWrite<UpdateLocationInput>({
  optimistic: (params) => {
    const input = updateLocationInputSchema.parse(params);
    locations().update(resourceIdForDomainId(input.id), (draft) => {
      if (input.name !== undefined) draft.name = input.name;
      if (input.status !== undefined) draft.status = input.status;
      if (input.description !== undefined) draft.description = input.description ?? undefined;
      if (input.type !== undefined) draft.type = input.type;
      if (input.physicalType !== undefined) draft.physicalType = input.physicalType;
      if (input.address !== undefined) draft.address = input.address;
      if (input.telecom !== undefined) draft.telecom = input.telecom;
      if (input.hoursOfOperation !== undefined) draft.hoursOfOperation = input.hoursOfOperation;
      if (input.partOfId === null) {
        draft.partOf = undefined;
        draft.partOfDomainId = undefined;
      } else if (input.partOfId !== undefined) {
        draft.partOf = { identifier: { system: LOCATION_ENTITY_ID_SYSTEM, value: input.partOfId } };
        draft.partOfDomainId = input.partOfId;
      }
    });
  },
  persist: async (params, options) => {
    await getTRPCClient().resources.updateLocation.mutate(params, options);
  },
});

/**
 * Intent: "deregister a location"
 *
 * Marks it inactive rather than dropping the row: the server does the same, so
 * a delete here would be undone the moment the subscription echoed the
 * Location back. Lists hide inactive locations, so it still disappears.
 */
export const deregisterLocation = defineWrite<{ id: string }>({
  optimistic: ({ id }) => {
    locations().update(resourceIdForDomainId(id), (draft) => {
      draft.status = "inactive";
    });
    const schedules = getDataStoreClient().getCollection("schedule");
    for (const [key, row] of schedules.entries()) {
      if (row.actor.some((actor) => actor.identifier?.value === id))
        schedules.update(key, (draft) => {
          draft.active = false;
        });
    }
  },
  persist: async ({ id }, options) => {
    await getTRPCClient().resources.deregisterLocation.mutate({ id }, options);
  },
});
